import { useCallback, useEffect, useMemo } from "react"

import { ApiQueryName } from "@/models/api"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { createQueryKey } from "../../utils/createQueryKey"
import type { SigmaAccessToken } from "../sigma.types"
import {
  getSigmaAccessToken,
  refreshSigmaAccessToken,
} from "./useSigmaAccessTokenUtils"

const SIGMA_REFRESH_BUFFER_SECONDS = 60
const SIGMA_MIN_REFRESH_INTERVAL_MS = 30_000
let sigmaRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null
let sigmaOwnerSubscribers = 0
let pendingSigmaRefresh: Promise<SigmaAccessToken | null> | null = null

export const sigmaAccessTokenQueryKey = createQueryKey(
  ApiQueryName.SigmaAccessToken,
  "getOne"
)

export const getSigmaRefreshIntervalMs = (expiresIn: number): number => {
  const refreshInMs = (expiresIn - SIGMA_REFRESH_BUFFER_SECONDS) * 1000

  return Math.max(refreshInMs, SIGMA_MIN_REFRESH_INTERVAL_MS)
}

const clearSigmaRefreshTimer = () => {
  if (sigmaRefreshTimeoutId) {
    clearTimeout(sigmaRefreshTimeoutId)
    sigmaRefreshTimeoutId = null
  }
}

export const useSigmaAccessTokenOwner = () => {
  const queryClient = useQueryClient()

  const tokenQuery = useQuery({
    gcTime: Infinity,
    queryKey: sigmaAccessTokenQueryKey,
    queryFn: async (): Promise<SigmaAccessToken | null> =>
      getSigmaAccessToken(),
    staleTime: Infinity,
  })

  const scheduleNextRefresh = useCallback(
    (refreshFn: () => Promise<SigmaAccessToken | null>) => {
      if (sigmaOwnerSubscribers === 0) {
        clearSigmaRefreshTimer()
        return
      }

      const token = queryClient.getQueryData<SigmaAccessToken | null>(
        sigmaAccessTokenQueryKey
      )

      if (!token) {
        clearSigmaRefreshTimer()
        return
      }

      clearSigmaRefreshTimer()

      sigmaRefreshTimeoutId = setTimeout(() => {
        refreshFn().catch((error: unknown) => {
          console.error(error)
        })
      }, getSigmaRefreshIntervalMs(token.expiresIn))
    },
    [queryClient]
  )

  const refreshAccessToken =
    useCallback(async (): Promise<SigmaAccessToken | null> => {
      if (pendingSigmaRefresh) {
        return pendingSigmaRefresh
      }

      pendingSigmaRefresh = (async () => {
        const existingToken = queryClient.getQueryData<SigmaAccessToken | null>(
          sigmaAccessTokenQueryKey
        )

        if (!existingToken?.refreshToken) {
          const freshToken = await getSigmaAccessToken()
          queryClient.setQueryData(sigmaAccessTokenQueryKey, freshToken)
          return freshToken
        }

        const refreshedToken = await refreshSigmaAccessToken(
          existingToken.refreshToken
        )

        if (refreshedToken) {
          queryClient.setQueryData(sigmaAccessTokenQueryKey, refreshedToken)
          return refreshedToken
        }

        const fallbackToken = await getSigmaAccessToken()
        queryClient.setQueryData(sigmaAccessTokenQueryKey, fallbackToken)

        return fallbackToken
      })()

      try {
        return await pendingSigmaRefresh
      } finally {
        pendingSigmaRefresh = null
        scheduleNextRefresh(refreshAccessToken)
      }
    }, [queryClient, scheduleNextRefresh])

  useEffect(() => {
    sigmaOwnerSubscribers += 1
    scheduleNextRefresh(refreshAccessToken)

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        refreshAccessToken().catch((error: unknown) => {
          console.error(error)
        })
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityOrFocus)
    window.addEventListener("focus", handleVisibilityOrFocus)

    return () => {
      sigmaOwnerSubscribers -= 1
      if (sigmaOwnerSubscribers <= 0) {
        sigmaOwnerSubscribers = 0
        clearSigmaRefreshTimer()
      }
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus)
      window.removeEventListener("focus", handleVisibilityOrFocus)
    }
  }, [refreshAccessToken, scheduleNextRefresh])

  useEffect(() => {
    scheduleNextRefresh(refreshAccessToken)
  }, [refreshAccessToken, scheduleNextRefresh, tokenQuery.data?.expiresIn])

  const tokenData =
    queryClient.getQueryData<SigmaAccessToken | null>(
      sigmaAccessTokenQueryKey
    ) ??
    tokenQuery.data ??
    null

  return useMemo(
    () => ({
      refreshSigmaToken: refreshAccessToken,
      sigmaAccessTokenData: tokenData,
      sigmaAccessTokenIsFetched: tokenQuery.isFetched,
    }),
    [refreshAccessToken, tokenData, tokenQuery.isFetched]
  )
}
